
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Category } from '../types';

interface FilterAccordionProps {
  categories: Category[];
  selectedFilters: string[];
  onToggleFilter: (filterId: string, categoryId: string) => void;
  isVisible: boolean;
  isDarkMode?: boolean;
}

const FilterAccordion: React.FC<FilterAccordionProps> = ({ 
  categories, 
  selectedFilters, 
  onToggleFilter,
  isVisible,
  isDarkMode
}) => {
  const [openCategories, setOpenCategories] = useState<string[]>(() => {
     try {
       const saved = localStorage.getItem('accordion_open_state');
       return saved ? JSON.parse(saved) : [];
     } catch { return []; }
  });

  useEffect(() => {
     localStorage.setItem('accordion_open_state', JSON.stringify(openCategories));
  }, [openCategories]);

  if (!isVisible) return null;

  const toggleCategory = (catId: string) => {
    setOpenCategories(prev => prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]);
  };

  return (
    <div className="space-y-2">
      {categories.filter(c => c.isVisible && c.filters.some(f => f.isActive)).map(category => {
        const isOpen = openCategories.includes(category.id);
        const activeFiltersInCat = category.filters.filter(f => selectedFilters.includes(f.id));
        
        // Extraemos el color base para generar el resalte suave
        const colorClass = category.color || 'text-blue-600';
        const colorName = colorClass.split('-')[1] || 'blue';
        const colorBase = `bg-${colorName}-600`;
        
        // Estilos dinámicos: eliminamos hovers grises y usamos el color de la categoría
        const headerBg = isOpen 
          ? (isDarkMode ? `bg-${colorName}-900/20` : `bg-${colorName}-100/40`) 
          : (isDarkMode ? `hover:bg-${colorName}-900/10` : `hover:bg-${colorName}-50`);
          
        const labelColor = isOpen
          ? (isDarkMode ? `text-${colorName}-400` : `text-${colorName}-700`)
          : (isDarkMode ? 'text-gray-400' : 'text-gray-600');

        return (
          <div key={category.id} className={`rounded-2xl border overflow-hidden transition-all duration-300 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
            <button 
              onClick={() => toggleCategory(category.id)}
              className={`w-full flex items-center justify-between py-3 px-4 transition-all ${headerBg}`}
            >
              <div className="flex items-center space-x-3">
                 <div className={`w-2.5 h-2.5 rounded-full shadow-sm ${colorBase}`}></div>
                 <span className={`font-black text-[10px] uppercase tracking-[0.15em] transition-colors ${labelColor}`}>
                    {category.name}
                 </span>
              </div>
              <div className="flex items-center space-x-2">
                {activeFiltersInCat.length > 0 && !isOpen && (
                  <span className={`text-[9px] px-2 py-0.5 rounded-lg font-black uppercase tracking-widest ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                    {activeFiltersInCat.length} ACTIVO
                  </span>
                )}
                {isOpen ? <ChevronUp size={14} className="text-gray-400 opacity-50" /> : <ChevronDown size={14} className="text-gray-400 opacity-50" />}
              </div>
            </button>
            
            {isOpen && (
              <div className={`p-4 border-t border-dashed animate-in slide-in-from-top-1 fade-in duration-200 ${isDarkMode ? 'bg-[#1e2024]/30 border-gray-700' : 'bg-white border-gray-100'}`}>
                <div className="flex flex-wrap gap-2">
                  <button
                     onClick={() => onToggleFilter('ALL', category.id)}
                     className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all 
                       ${activeFiltersInCat.length === 0 
                         ? `${colorBase} border-transparent text-white shadow-lg` 
                         : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-500' : 'bg-white border-gray-200 text-gray-400')
                       }`}
                  >
                    Todos
                  </button>

                  {category.filters.filter(f => f.isActive).map(filter => {
                    const isSelected = selectedFilters.includes(filter.id);
                    return (
                      <button
                        key={filter.id}
                        onClick={() => onToggleFilter(filter.id, category.id)}
                        className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all
                          ${isSelected 
                            ? `${colorBase} border-transparent text-white shadow-lg` 
                            : (isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300')
                          }`}
                      >
                        {filter.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FilterAccordion;
